import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClipboardActivity } from '@renderer/hooks/activity/useClipboardActivity.ts'
import { useScreenshotActivity } from '@renderer/hooks/activity/useScreenshotActivity.ts'
import { useSelectedTextActivity } from '@renderer/hooks/activity/useSelectedTextActivity.ts'
import { useGlobalState } from '@highlight/ipc/react'
import {
  useAudioNoteSavedNote,
  useIsAudioNoteActive,
  useIsRecordingAudioNote,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAttachmentPermission } from '@renderer/hooks/settings/useAttachmentPermission'
import MeetingIcon from '@renderer/components/Icons/MeetingIcon'
import { AudioActivity } from '@renderer/chat/components'
import { useIsOnboarded } from '@renderer/hooks/useIsOnboarded'
import { useAttachmentNotifications } from '@renderer/hooks/settings/use-attachment-notifications'
import { useLdFlag } from '@renderer/chat'
import { Mic, Plus, ClipboardType, Image, TextCursor } from 'lucide-react'

export const getActivityElement = (activity: 'audio' | 'pushToTalk' | 'clipboard' | 'image' | 'selectedText' | '') => {
  switch (activity) {
    case 'audio':
      return <AudioActivity />
    case 'clipboard':
      return <ClipboardType size={20} className="text-pink-subtle" />
    case 'image':
      return <Image size={20} className="text-brand-9" />
    case 'selectedText':
      return <TextCursor size={20} className="text-yellow-subtle" />
    default:
      return
  }
}

type ScreenshotState = {
  screenshotUrl?: string
  timestamp: number
}

type ClipboardState = {
  text?: string
  timestamp: number
}

type SelectedTextState = {
  detected: boolean
  timestamp: number
}

const useCurrentActivity = () => {
  // Audio activity state
  const isAudioNoteActive = useIsAudioNoteActive()
  const savedNote = useAudioNoteSavedNote()
  const isRecording = useIsRecordingAudioNote()
  const { canDetect: canDetectSelectedText } = useAttachmentPermission('selected-text')
  const { canDetect: canDetectScreenshot } = useAttachmentPermission('screenshots')
  const { canDetect: canDetectClipboard } = useAttachmentPermission('clipboard-text')
  const { notificationsEnabled } = useAttachmentNotifications()
  const [pushToTalkActive] = useGlobalState<boolean>('ptt.pressed')
  const showNotificationsWhenDisabled = useLdFlag('assistant-detected-state-notifications', true)

  // Clipboard activity state
  const { detectedClipboardText, clearDetectedClipboard } = useClipboardActivity()
  const [clipboardState, setClipboardState] = useState<ClipboardState | null>(null)

  // Screenshot activity state
  const { detectedScreenshot, clearDetectedScreenshot } = useScreenshotActivity()
  const [screenshotState, setScreenshotState] = useState<ScreenshotState | null>(null)

  // Selected text activity state
  const { selectedText, clearSelectedText } = useSelectedTextActivity()
  const [selectedTextState, setSelectedTextState] = useState<SelectedTextState | null>(null)

  // We don't clear activity state after 5 seconds when the shortcuts menu is open,
  // so we need to manually track which detected state was most recently set and return that,
  // versus returning in a particular order base don which are available (previous behavior)
  useEffect(() => {
    if (canDetectClipboard && notificationsEnabled && showNotificationsWhenDisabled) {
      setClipboardState({
        text: detectedClipboardText,
        timestamp: Date.now(),
      })
    }
  }, [detectedClipboardText, canDetectClipboard, notificationsEnabled, showNotificationsWhenDisabled])

  useEffect(() => {
    if (canDetectScreenshot && notificationsEnabled && showNotificationsWhenDisabled) {
      setScreenshotState({
        screenshotUrl: detectedScreenshot,
        timestamp: Date.now(),
      })
    }
  }, [detectedScreenshot, canDetectScreenshot, notificationsEnabled, showNotificationsWhenDisabled])

  useEffect(() => {
    if (canDetectSelectedText && notificationsEnabled && showNotificationsWhenDisabled) {
      setSelectedTextState({
        detected: selectedText !== undefined,
        timestamp: Date.now(),
      })
    }
  }, [selectedText, canDetectSelectedText, notificationsEnabled, showNotificationsWhenDisabled])

  const isOnboarded = useIsOnboarded()
  const pttStartTimeRef = useRef<number | null>(null)
  const [pttHeldLongEnough, setPttHeldLongEnough] = useState(false)

  useEffect(() => {
    if (pushToTalkActive) {
      if (!pttStartTimeRef.current) {
        pttStartTimeRef.current = Date.now()
      }
      const checkTime = () => {
        if (pttStartTimeRef.current && Date.now() - pttStartTimeRef.current >= 500) {
          setPttHeldLongEnough(true)
        }
      }
      const timer: NodeJS.Timeout = setTimeout(checkTime, 510)
      return () => clearTimeout(timer)
    } else {
      pttStartTimeRef.current = null
      setPttHeldLongEnough(false)
    }
  }, [pushToTalkActive])

  const activity = useMemo<'audio' | 'pushToTalk' | 'clipboard' | 'image' | 'selectedText' | ''>(() => {
    if (!isOnboarded) {
      return ''
    }

    if (pushToTalkActive && pttHeldLongEnough) {
      return 'pushToTalk'
    }

    const activityTimestamps = [
      { type: 'image', time: screenshotState?.timestamp || 0, hasContent: !!screenshotState?.screenshotUrl },
      { type: 'clipboard', time: clipboardState?.timestamp || 0, hasContent: !!clipboardState?.text },
      { type: 'selectedText', time: selectedTextState?.timestamp || 0, hasContent: !!selectedTextState?.detected },
    ]

    // Filter activities that have content and find most recent
    const mostRecentActivity = activityTimestamps
      .filter((activity) => activity.hasContent)
      .sort((a, b) => b.time - a.time)[0]

    if (mostRecentActivity) {
      return mostRecentActivity.type as 'image' | 'clipboard' | 'selectedText'
    }

    if (isRecording || isAudioNoteActive) {
      return 'audio'
    }
    return ''
  }, [
    isRecording,
    clipboardState,
    screenshotState,
    selectedTextState,
    isOnboarded,
    pushToTalkActive,
    pttHeldLongEnough,
    isAudioNoteActive,
  ])

  const clearActivity = useCallback(
    (clear: 'clipboard' | 'image' | 'selectedText') => {
      if (clear === 'image') {
        clearDetectedScreenshot()
      } else if (clear === 'clipboard') {
        clearDetectedClipboard()
      } else if (clear === 'selectedText') {
        clearSelectedText()
      }
    },
    [clearDetectedClipboard, clearDetectedScreenshot, clearSelectedText],
  )

  const hasActivity = useMemo(() => !!activity?.length, [activity])

  const activityElement = useMemo<ReactElement | undefined>(() => {
    if (isAudioNoteActive) {
      if (savedNote?.meeting?.app?.name) {
        return <MeetingIcon appName={savedNote.meeting.app.name} size={20} />
      } else if (savedNote) {
        return <Mic size={20} className="text-green" />
      } else {
        return (
          <div className="pb-0.5">
            <AudioActivity />
          </div>
        )
      }
    }

    if (!isAudioNoteActive) {
      return getActivityElement(activity)
    }

    return <Plus size={24} className="text-brand-9" />
  }, [activity, isAudioNoteActive, savedNote])

  return useMemo(() => {
    return {
      activity,
      hasActivity,
      clearActivity,
      activityElement,
    }
  }, [activity, hasActivity, clearActivity, activityElement])
}

export default useCurrentActivity
